import { useApolloClient } from "@apollo/client";
import { Avatar, ScrollArea, Text, useMantineTheme } from "@mantine/core";
import { GetLocationPage } from "src/api/graphql/queries/Location";
import {
  GetLocationPageQuery,
  GetLocationPageQueryVariables,
} from "src/generated/graphql";
import { KapexTable, FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useTogglableTheme } from "src/utils/theme";
import { useCallback, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { BiUserCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_ORDER,
  EMPLOYEE_ROLE_OBJ,
  ROUTES,
  ROUTE_NAMES,
} from "src/utils/constants";
import { Button } from "@mantine/core";
import { useModals } from "@mantine/modals";
import CreateLocationForm from "./CreateLocationForm";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import useWritePermission from "src/hooks/usePermission";

export type LocationData = ArrayElement<
  GetLocationPageQuery["locationPage"]["items"]
>;

const LocationList = () => {
  const modals = useModals();
  const navigate = useNavigate();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgLocations);

  const { isDarkMode, setCurrentPageTitle } = useContext(AppContext);
  const theme = useMantineTheme();
  const toggleTheme = useTogglableTheme(isDarkMode, theme);
  const { query } = useApolloClient();

  const getLocations = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<LocationData>> => {
    let { data } = await query<
      GetLocationPageQuery,
      GetLocationPageQueryVariables
    >({
      query: GetLocationPage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
      },
    });

    if (data.locationPage) {
      return {
        rowCount: data.locationPage.totalRows,
        rowData: data.locationPage.items as LocationData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.LOCATION);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Location",
      children: (
        <CreateLocationForm
          handleClose={() => {
            modals.closeAll();
          }}
        />
      ),
    });
  }, []);

  const handleNavigation = (id: string): void => {
    navigate(`${ROUTES.LOCATION}/${id}`);
  };

  return (
    <div>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={handleModal}>
          Create +
        </Button>
      )}
      <ScrollableDataGrid<LocationData>
        topMargin={56}
        columnConfigs={[
          {
            field: "logoUrl",
            name: "",
            sortable: false,
            cellRenderer: (rowItem) => {
              return rowItem.logoUrl ? (
                <Avatar
                  radius="xl"
                  sx={{
                    ":root": {
                      justifyContent: "center",
                      alignItems: "center",
                      display: "flex",
                    },
                  }}
                  size="sm"
                  src={rowItem.logoUrl}
                />
              ) : (
                <div style={{ padding: 0, borderRadius: 18 }}>
                  <BiUserCircle size={26} />
                </div>
              );
            },
          },
          {
            field: "name",
            name: "Name",
            filter: {
              field: "name",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.name}
              </Text>
            ),
          },
          {
            field: "address",
            name: "Address",
            filter: {
              field: "address",
              type: "text",
            },
            sortable: true,
            cellRenderer: (rowItem) => (
              <Text
                weight={500}
                onClick={() => handleNavigation(rowItem.id)}
                style={{ cursor: "pointer" }}
              >
                {rowItem.address}
              </Text>
            ),
          },
          {
            field: "createdDate",
            name: "Created",
            filter: {
              field: "createdDate",
              type: "dateRange",
            },
            sortable: true,
            valueParser: (rowItem) => {
              return new Date(rowItem.createdDate).toDateString();
            },
          },
        ]}
        dataFetch={getLocations}
      />
    </div>
  );
};

export default LocationList;
